import type { ComponentType, ReactNode } from 'react'
import { Tooltip } from './Tooltip'
import './tool.css'
import './segmented-control.css'

type IconType = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>

/**
 * A segmented switch — pick one of a few options (table vs cards, list vs
 * board). It reads as one inset control; the active segment lifts onto a raised
 * chip with an indigo-touched label, never a heavy fill.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  size = 'md',
}: {
  options: {
    value: T
    label?: ReactNode
    icon?: IconType
    ariaLabel?: string
    /**
     * A name shown on hover and focus, for a segment drawn as its icon alone —
     * where its label has no room to show.
     */
    tooltip?: string
  }[]
  value: T
  onChange: (value: T) => void
  ariaLabel: string
  /** `sm` matches the editor bar's own tool height, so a switch sitting among
   *  them reads as one of the set rather than a control dropped onto it. */
  size?: 'sm' | 'md'
}) {
  return (
    <div
      className={`segmented segmented--${size}`}
      role="group"
      aria-label={ariaLabel}
    >
      {options.map((o) => {
        const Icon = o.icon
        const isActive = o.value === value
        const seg = (
          <button
            key={o.value}
            type="button"
            className="segmented__seg"
            data-active={isActive ? '' : undefined}
            aria-pressed={isActive}
            aria-label={o.ariaLabel}
            onClick={() => onChange(o.value)}
          >
            {Icon ? <Icon size={16} aria-hidden /> : null}
            {o.label ? <span>{o.label}</span> : null}
          </button>
        )
        return o.tooltip ? (
          <Tooltip key={o.value} label={o.tooltip} placement="top">
            {seg}
          </Tooltip>
        ) : (
          seg
        )
      })}
    </div>
  )
}
